import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Lock } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const SERVICE_TYPE_MAP: Record<string, string> = {
  "online": "online-gp",
  "online-pediatrics": "online-pediatrics",
};

const Pricing = () => {
  const [loadingService, setLoadingService] = useState<string | null>(null);

  const handleBooking = async (serviceType: string) => {
    const checkoutType = SERVICE_TYPE_MAP[serviceType];
    if (!checkoutType) return;

    setLoadingService(serviceType);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { serviceType: checkoutType },
      });

      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err: any) {
      toast.error("Payment error", { description: err.message || "Please try again or contact us via WhatsApp." });
    } finally {
      setLoadingService(null);
    }
  };

  const plans = [
    {
      name: "Online GP (Adults)",
      price: "USD 50",
      priceUSD: "~ R$ 256 BRL / ~ €46 EUR",
      description: "Video consultation from anywhere in Brazil",
      features: [
        "Medical evaluation & diagnosis",
        "Digital prescription (if needed)",
        "Medical certificates",
        "Follow-up recommendations",
        "Available throughout Brazil"
      ],
      popular: false,
      highlight: true,
      serviceType: "online"
    },
    {
      name: "Online Pediatrics",
      price: "USD 50",
      priceUSD: "~ R$ 256 BRL / ~ €46 EUR",
      description: "Specialized care for children (all ages) - Available throughout Brazil",
      features: [
        "Pediatric consultation",
        "Child-friendly approach",
        "Parent guidance included",
        "Growth & development advice",
        "School/travel certificates"
      ],
      popular: false,
      highlight: false,
      serviceType: "online-pediatrics"
    }
  ];

  return (
    <section id="pricing" className="py-20 bg-background">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-foreground mb-4">
            Transparent Pricing
          </h2>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto">
            Clear, upfront pricing with no hidden fees. All major credit and debit cards accepted.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-12">
          {plans.map((plan, index) => (
            <Card key={index} className={`relative h-full transition-transform ${plan.highlight ? 'ring-2 ring-primary md:scale-105 md:shadow-xl z-10' : ''}`}>
              <CardHeader className="text-center pb-6">
                <CardTitle className="text-2xl mb-2">{plan.name}</CardTitle>
                <div className="mb-2">
                  <span className="text-4xl font-bold text-primary">{plan.price}</span>
                  <div className="text-sm text-text-secondary mt-1">{plan.priceUSD}</div>
                </div>
                <CardDescription className="text-base">
                  {plan.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <Check className="h-5 w-5 text-accent mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-text-secondary">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  className="w-full" 
                  variant={plan.highlight ? "default" : "outline"}
                  size="lg"
                  onClick={() => handleBooking(plan.serviceType)}
                  disabled={loadingService === plan.serviceType}
                >
                  {loadingService === plan.serviceType ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing...</>
                  ) : (
                    `Book ${plan.name}`
                  )}
                </Button>
                <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-muted-foreground text-center">
                  <Lock className="h-3.5 w-3.5 flex-shrink-0" />
                  Secure 256-bit encrypted Stripe checkout · Instant WhatsApp booking right after payment
                </p>
              </CardContent>
            </Card>
          ))}
        </div>


        {/* Important Notice */}
        <div className="bg-destructive/10 border border-destructive/20 p-6 rounded-lg mb-6 max-w-4xl mx-auto">
          <div className="text-center">
            <h3 className="font-bold text-destructive mb-2">⚠️ Important Notice</h3>
            <p className="text-destructive">
              <strong>Not an emergency service.</strong> In medical emergencies, call 192 (SAMU) immediately.
            </p>
          </div>
        </div>

        {/* Prescription & Documentation Policy */}
        <div className="bg-card border border-border p-6 rounded-lg mb-12 max-w-4xl mx-auto">
          <div className="text-center">
            <h3 className="font-bold text-foreground mb-2">Prescriptions & Medical Documentation</h3>
            <p className="text-sm text-muted-foreground">
              <strong>Prescriptions and medical certificates are issued only after a live consultation, when clinically indicated by the physician.</strong> Rio Med Care does not sell, dispense, or deliver any medications. Prescriptions can be filled at any licensed pharmacy in Brazil of your choice.
            </p>
            <p className="text-sm text-muted-foreground mt-3">
              When appropriate, your consultation can include digital prescriptions, medical certificates, fitness-to-travel letters, sick leave documentation and insurance receipts in English and Portuguese.
            </p>
          </div>
        </div>

        {/* Payment & Insurance Info */}
        <div className="text-center space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-card p-6 rounded-lg">
              <h3 className="font-semibold mb-3">💳 Payment Methods</h3>
              <div className="flex justify-center space-x-4 mb-3">
                <span className="bg-primary/10 px-3 py-1 rounded text-sm">Visa</span>
                <span className="bg-primary/10 px-3 py-1 rounded text-sm">Mastercard</span>
                <span className="bg-primary/10 px-3 py-1 rounded text-sm">AmEx</span>
              </div>
              <p className="text-sm text-muted-foreground">Credit and debit cards accepted</p>
            </div>
            <div className="bg-card p-6 rounded-lg">
              <h3 className="font-semibold mb-3">🏥 Insurance Coverage</h3>
              <p className="text-sm text-muted-foreground mb-2">
                <strong>Receipts available for insurance reimbursement requests.</strong>
              </p>
              <p className="text-sm text-muted-foreground">
                We provide detailed documentation in English and Portuguese for international health insurance claims and reimbursement requests.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;